import { apiFetch, APIResponse } from '@m/utils/http';
import { setLastVehicleWasPreviouslyAdded } from 'apps/customer/state/onboard';
import { fetchTodoItems } from 'apps/customer/state/todo';
import { fetchCustomerVisits } from 'apps/customer/state/visit';
import { trackFailedAddVehicle } from 'apps/customer/utils/heapOnboardTracking';
import { createEffect, createEvent } from 'effector';

import type { AddedVia } from '@m/types/grpc/MemberOnboardingSource';

type failPayload = {
  fallbackMessage: string;
  response: APIResponse;
};

function trackAddOrUpdateVehicleFailure({ response, fallbackMessage }: failPayload) {
  const { search, errorCodes } = {
    search: response?.data?.search || 'null',
    errorCodes: response?.data?.errorCodes || [],
  };

  const mappedCodes = errorCodes.reduce((acc: { [x: string]: string }, key: string | number) => {
    acc[key] = 'true';
    return acc;
  }, {});

  trackFailedAddVehicle({
    addVehicleErrorMessage: response?.error || fallbackMessage,
    search: search || '',
    ...mappedCodes,
  });
}

export const fetchVehicles = createEffect({
  handler: () => apiFetch('/customer/vehicles'),
});

export const fetchVehicleMakes = createEffect({
  handler: () => apiFetch('/v2/vehicle/makes'),
});

export const fetchVehicleModels = createEffect({
  handler: async ({ makeId }: { makeId: number }) => {
    const response = await apiFetch('/vehicle/models', { params: { makeId } });

    if (response.success) {
      response.data.makeId = makeId;
    }

    return response;
  },
});

export const fetchVehicleYears = createEffect({
  handler: async ({ modelId }: { modelId: number }) => {
    const response = await apiFetch('/vehicle/model-years', { params: { modelId } });

    if (response.success) {
      response.data.modelId = modelId;
    }

    return response;
  },
});

/**
 * We need to fetch updated todos and possibly visits after making changes to a user's account
 * since there might be more actionable tasks a customer needs to do
 *
 * Fetching visits is required during sign up
 * Will prevent a jump from "About Metropolis"
 * to "Open Visit" if user comes from scanning QR validation.
 * @param refreshVisits - boolean
 */
async function updateClientState({ refreshVisits }: { refreshVisits?: boolean }) {
  const refreshTodoPromise = fetchTodoItems();
  let fetchCustomerVisitsPromise;
  if (refreshVisits) {
    fetchCustomerVisitsPromise = fetchCustomerVisits();
  }
  return Promise.all([refreshTodoPromise, fetchCustomerVisitsPromise]);
}

export interface AddVehiclePayload {
  addedVia?: AddedVia;
  color?: string | null;
  isTemporaryVehicle?: boolean | null;
  licensePlateStateId?: number | string | null;
  licensePlateText?: string;
  makeId?: number | string | null;
  modelId?: number | string | null;
  modelYearId?: number | string | null;
  refreshVisitsOnSuccess?: boolean;
  suggestedEventConfirmed?: boolean | null;
  suggestedEventUuid?: string | null;
  temporaryVehicleEndTime?: number | null;
}

export interface UpdateVehiclePayload extends AddVehiclePayload {
  vehicleId: number;
}

export const addVehicle = createEffect({
  handler: async ({
    licensePlateText,
    licensePlateStateId,
    makeId,
    modelId,
    modelYearId,
    color,
    isTemporaryVehicle,
    temporaryVehicleEndTime,
    refreshVisitsOnSuccess,
    suggestedEventUuid,
    suggestedEventConfirmed,
    addedVia,
  }: AddVehiclePayload) => {
    const response = await apiFetch('/customer/vehicle', {
      method: 'POST',
      body: {
        licensePlate: {
          state: { id: licensePlateStateId },
          text: licensePlateText,
        },
        makeId,
        modelId,
        modelYearId,
        color,
        isTemporaryVehicle,
        temporaryVehicleEndTime,
        suggestedEventUuid: suggestedEventUuid || null,
        suggestedEventConfirmed,
        addedVia,
      },
    });

    let todoResp;
    if (response.success) {
      [todoResp] = await updateClientState({ refreshVisits: refreshVisitsOnSuccess });
      response.data.todoItems = todoResp?.data?.todoItems;
      if (response?.data?.vehicle) {
        const { vehicle } = response.data;
        setLastVehicleWasPreviouslyAdded(vehicle.sharedVehicleRole.name === 'Pending Shared');
      }
    } else {
      trackAddOrUpdateVehicleFailure({
        response,
        fallbackMessage: 'Error: Failed to add vehicle',
      });
    }

    return response;
  },
});

export const updateVehicle = createEffect({
  handler: async ({
    vehicleId,
    licensePlateText,
    licensePlateStateId,
    makeId,
    modelId,
    modelYearId,
    color,
    isTemporaryVehicle,
    temporaryVehicleEndTime,
    refreshVisitsOnSuccess,
  }: UpdateVehiclePayload) => {
    const bodyWithoutLP = {
      makeId,
      modelId,
      modelYearId,
      color,
      isTemporaryVehicle,
      temporaryVehicleEndTime,
    };

    const body =
      licensePlateText || licensePlateStateId
        ? {
            ...bodyWithoutLP,
            licensePlate: {
              state: { id: licensePlateStateId },
              text: licensePlateText,
            },
          }
        : bodyWithoutLP;

    const response = await apiFetch(`/customer/vehicle/${vehicleId}`, {
      method: 'PUT',
      body,
    });

    let todoResp;
    if (response.success) {
      [todoResp] = await updateClientState({ refreshVisits: refreshVisitsOnSuccess });
      response.data.todoItems = todoResp?.data?.todoItems;
    } else {
      trackAddOrUpdateVehicleFailure({
        response,
        fallbackMessage: 'Error: Failed to update vehicle',
      });
    }

    return response;
  },
});

export const updateVehicleType = createEffect({
  handler: async ({
    vehicleId,
    color,
    makeId,
    modelId,
    licensePlate, // returns req err without LP
  }: {
    color?: string | null;
    licensePlate: {
      state: { id: number | string | null };
      text?: string;
    };
    makeId: number | null;
    modelId: number | null;
    vehicleId: number;
  }) => {
    const response = await apiFetch(`/customer/vehicle/${vehicleId}`, {
      method: 'PUT',
      body: {
        licensePlate,
        makeId,
        modelId,
        color,
      },
    });

    if (response.success) {
      await updateClientState({ refreshVisits: true });
    }

    return response;
  },
});

export const removeVehicle = createEffect({
  handler: ({ vehicleId }: { vehicleId: number }) =>
    apiFetch(`/customer/vehicle/${vehicleId}`, { method: 'DELETE' }),
});

// userId used when approving, phoneNumber when adding yourself
export const addSharedDriver = createEffect({
  handler: async ({
    vehicleId,
    phoneNumber,
    userId,
  }: {
    phoneNumber?: string;
    userId?: number;
    vehicleId: number;
  }) => {
    const response = await apiFetch(`/customer/vehicle/${vehicleId}/add-driver`, {
      method: 'POST',
      body: {
        phoneNumber,
        userId,
      },
    });

    return response;
  },
});

export const removeSharedDriver = createEffect({
  handler: async ({ vehicleId, userId }: { userId: number; vehicleId: number }) => {
    const response = await apiFetch(`/customer/vehicle/${vehicleId}/remove-driver/${userId}`, {
      method: 'DELETE',
    });

    return response;
  },
});

export const promoteSharedDriverToPrimary = createEffect({
  handler: async ({ vehicleId, userId }: { userId: number; vehicleId: number }) => {
    const response = await apiFetch(
      `/customer/vehicle/${vehicleId}/request-promote-driver/${userId}`,
      { method: 'POST' },
    );

    return response;
  },
});

export const acceptBecomingPrimaryDriver = createEffect({
  handler: async ({
    vehicleId,
    requestId,
    accepted,
  }: {
    accepted: boolean;
    requestId: number;
    vehicleId: number;
  }) => {
    const response = await apiFetch(
      `/customer/vehicle/${vehicleId}/confirm-request/${requestId}?confirm=${accepted}`,
      { method: 'POST' },
    );

    return response;
  },
});

export const resetVehicleAsyncStatuses = createEvent();
